import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LoginService } from "../../services/LoginService";
import { UserContext } from "../../context/UserContext";
import { User } from "../../interfaces/user/User";
import UserService from "../../services/UserService";
import { useCookies } from "react-cookie";
import {
  Alert,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
  Link,
} from "@mui/material";
import lightlogo from "../../assets/images/infiniwell_PMS_logo-id.png";
import darklogo from "../../assets/images/infiniwell_PMS_logo-id.png";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const Login: React.FC = () => {
  //const [credentials, setCredentials] = useState<Credentials>({ username: '', password: '' });
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  const [error, setError] = useState<string>("");
  const history = useNavigate();
  const userContext = useContext(UserContext);
  const [, setCookie] = useCookies();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const theme = useTheme();
  const { t } = useTranslation();

  if (!userContext) {
    throw new Error("UserContext not found");
  }

  const { setUser } = userContext;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
  };

  const performPasswordLogin = async () => {
    setError("");
    try {
      const response = await LoginService.login(credentials);
      if (response && response.access && response.refresh) {
        // Fetch user details with the access token
        const userDetails = await UserService.getMe(response.access);

        console.log("data from getme:", userDetails);
        var savedUser = localStorage.getItem("user");
        var autolog = "15"; // default to 15 minutes
        let is_auto_logout_enabled = true;

        if (savedUser) {
          const jj = JSON.parse(savedUser);
          autolog = jj.autoLogout || "15"; // Ensure defaulting to "15" if not set
        }

        console.log("Login: autoLogout is " + autolog + " minutes");

        // Combine user details from UserService with the newUser object
        const newUser: User = {
          ...userDetails, // Spread userDetails first to fill all known user data
          autoLogout: autolog,
          is_auto_logout_enabled,
          token: response, // Add token details
          mfaEnabled: false, // This could also come from userDetails if available
        };
        console.log("newUser is ", newUser);
        // Store the combined user data in local storage
        localStorage.setItem("user", JSON.stringify(newUser));

        // Set cookies for tokens
        let date = new Date();
        setCookie("access_token", response.access, {
          expires: new Date(date.setHours(date.getHours() + 24)),
        });
        setCookie("refresh_token", response.refresh, {
          expires: date,
        });

        // Update user context
        setUser(newUser);

        // useNavigate based on user role
        if (newUser.is_superadmin) {
          history("/sysmgmt");
        } else {
          history("/");
        }
      } else {
        setError("Invalid credentials or additional steps required.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Login failed. Please check your credentials and try again.");
    }
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = (credentials.username || "").trim().toLowerCase();

    if (email && email.includes("@")) {
      try {
        const ssoConfig = await LoginService.getSSOConfigByEmail(email, "login");
        if (ssoConfig && ssoConfig.sso_enabled && ssoConfig.sso_server) {
          await LoginService.startSSOFlow(ssoConfig, email, "login");
          return;
        }
      } catch (ssoError) {
        setError(LoginService.getSSOErrorMessage(ssoError));
        return;
      }
    }

    await performPasswordLogin();
  };

  const logo = theme.palette.mode === "dark" ? darklogo : lightlogo;
  return (
    <Grid
      container
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      height="75vh"
    >
      <Grid item>
        <img alt="Infiniwell-logo" src={logo} width="300" />
      </Grid>
      <Grid item>
        <form onSubmit={handleLogin}>
          <Grid
            container
            item
            mt={5}
            justifyContent={"space-around"}
            columnSpacing={1}
          >
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                name="username"
                value={credentials.username}
                onChange={handleChange}
                label={t("username")}
                variant="outlined"
                error={error !== ""}
                InputLabelProps={{
                  style: {
                    color: theme.palette.text.primary,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                sx={{ mt: isMobile ? 1 : 0 }}
                fullWidth
                name="password"
                value={credentials.password}
                onChange={handleChange}
                label={t("password")}
                variant="outlined"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  style: {
                    color: theme.palette.text.primary,
                  },
                }}
                error={error !== ""}
              />
              <Grid item mt={1}>
                <Link underline="hover" href="/forgot-password">
                  {t("forgot_password?")}
                </Link>
              </Grid>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                sx={{ mt: 2.3 }}
                color="primary"
                type="submit"
                variant="contained"
              >
                {t("login")}
              </Button>
            </Grid>
            {error !== "" && (
              <Grid item xs={12} mt={2}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default Login;
